import { useRef, useState, useEffect } from 'react';

/**
 * Smoothly interpolates a numeric value between real updates using
 * requestAnimationFrame. Gives the UI a fluid 60fps feel even when
 * server ticks arrive at 2-10fps.
 *
 * How it works:
 *   - When `targetValue` changes, the hook animates from the current
 *     displayed value to the new target over `durationMs`.
 *   - Uses ease-out cubic for natural deceleration.
 *   - If a new target arrives mid-animation, it seamlessly redirects
 *     from the current interpolated value (no jump).
 *   - Returns the current interpolated value at 60fps.
 */
export function useInterpolatedValue(
  targetValue: number | undefined,
  durationMs = 300,
): number | undefined {
  const [display, setDisplay] = useState(targetValue);
  const animRef = useRef<number | null>(null);
  const startValRef = useRef(targetValue ?? 0);
  const startTimeRef = useRef(0);
  const currentDisplayRef = useRef(targetValue ?? 0);
  const targetRef = useRef(targetValue);

  useEffect(() => {
    if (targetValue === undefined) return;

    // First value — snap immediately, no animation
    if (targetRef.current === undefined) {
      targetRef.current = targetValue;
      startValRef.current = targetValue;
      currentDisplayRef.current = targetValue;
      setDisplay(targetValue);
      return;
    }

    // Same value — skip
    if (targetValue === targetRef.current) return;

    // New target: animate from current displayed value
    targetRef.current = targetValue;
    startValRef.current = currentDisplayRef.current;
    startTimeRef.current = performance.now();

    if (animRef.current) cancelAnimationFrame(animRef.current);

    const animate = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / durationMs, 1);
      // Ease-out cubic: fast start, gentle landing
      const eased = 1 - Math.pow(1 - progress, 3);

      const val = startValRef.current + (targetRef.current! - startValRef.current) * eased;
      currentDisplayRef.current = val;
      setDisplay(val);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(animate);
  }, [targetValue, durationMs]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return display;
}

/**
 * Interpolates multiple numeric fields of an object simultaneously.
 * Non-numeric fields are passed through unchanged.
 *
 * Usage:
 *   const smoothBot = useInterpolatedObject(bot, ['virtual_equity', 'floating_pnl', 'combined_pnl']);
 */
export function useInterpolatedObject<T extends Record<string, any>>(
  target: T | undefined,
  fields: (keyof T)[],
  durationMs = 300,
): T | undefined {
  const [display, setDisplay] = useState(target);
  const animRef = useRef<number | null>(null);
  const startValsRef = useRef<Record<string, number>>({});
  const currentValsRef = useRef<Record<string, number>>({});
  const startTimeRef = useRef(0);
  const targetRef = useRef(target);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!target) return;

    // First value — snap immediately
    if (!initializedRef.current) {
      initializedRef.current = true;
      targetRef.current = target;
      for (const f of fields) {
        const val = typeof target[f] === 'number' ? target[f] : 0;
        startValsRef.current[f as string] = val;
        currentValsRef.current[f as string] = val;
      }
      setDisplay(target);
      return;
    }

    const prevTarget = targetRef.current;
    targetRef.current = target;

    // Check if any interpolated field actually changed
    const changed = fields.some(
      (f) =>
        typeof target[f] === 'number' &&
        target[f] !== prevTarget?.[f],
    );

    if (!changed) {
      // Non-numeric fields changed — preserve current interpolated values.
      // If an animation is running it reads targetRef (already updated)
      // and will pick up non-numeric changes on the next frame.
      if (animRef.current === null) {
        const merged = { ...target } as any;
        for (const f of fields) {
          const key = f as string;
          if (key in currentValsRef.current) {
            merged[key] = currentValsRef.current[key];
          }
        }
        setDisplay(merged as T);
      }
      return;
    }

    // Snapshot current displayed values as animation start
    for (const f of fields) {
      startValsRef.current[f as string] = currentValsRef.current[f as string] ?? 0;
    }
    startTimeRef.current = performance.now();

    // Cancel previous animation before starting new one
    if (animRef.current) cancelAnimationFrame(animRef.current);

    const animate = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      const interpolated: Record<string, any> = { ...targetRef.current };
      for (const f of fields) {
        const key = f as string;
        const start = startValsRef.current[key] ?? 0;
        const end = typeof targetRef.current![f] === 'number' ? targetRef.current![f] : 0;
        const val = start + (end - start) * eased;
        currentValsRef.current[key] = val;
        interpolated[key] = val;
      }

      setDisplay(interpolated as T);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      } else {
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(animate);

    // No cleanup here — animation cancellation is handled explicitly above.
    // Returning a cleanup would kill running animations on every target change
    // (new object ref per WebSocket tick), causing frame-gap flicker.
  }, [target, fields, durationMs]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return display;
}
